import { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	type TProductDataRegions,
	type TProductEdition,
} from '@atlassian/search-client/fetch-search-config';
import { type BackendExperiment, type ExperimentConfig } from '@atlassian/search-experiment/types';

import { QueryState } from '../../../common/constants/analytics';
import type { ProductKeys } from '../../../common/constants/product-keys';
import type { IAnalyticsSearchPageResults } from '../../../common/types';
import { searchResultsLoadUFOExperienceSuccess } from '../../../common/ui/ufo/experiences';
import { onAdvancedSearchResultsShown } from '../../../common/utils/events/advanced-search-results-shown';
import { onFullPageSearchOperational } from '../../../common/utils/events/full-page-search-operational';
import type {
	CommonAttributes,
	NonPrivacySafeAttributes,
	ResultShown,
} from '../../../common/utils/events/types';
import { serveJiraNINFpsNLQNavigation } from '../../../common/utils/experiments/experiment-exports';
import { INTENT_DETECTION } from '../../../common/utils/intent-detection';
import { getIsBulkConnectBannerShown } from '../../../common/utils/quick-find/analytics';
import { getExtensionData } from '../../../common/utils/relay/get-extension-data';
import { getAvailableSitesCount } from '../../../common/utils/sites';
import { useAnalytics } from '../../../controllers/analytics';
import { useSearchAndResultAnalyticsAttributes } from '../../../controllers/analytics/search-and-response-analytics';
import { useSocialSignalsAnalyticsAttributes } from '../../../controllers/analytics/social-signal-analytics';
import { useLogException } from '../../../controllers/error-boundary/utils';
import type { SelectedFiltersReturnType } from '../../../controllers/filters/selected/get-selected-filters';
import { getReferrerFromUrl, removeReferrerFromUrl } from '../../../controllers/remove-referrer';
import { useCurrentUserId, usePrimaryProduct } from '../../../controllers/store/bootstrap';

type useSearchResultsAnalyticsArgs<TEdge> = {
	are3pResultsShown: boolean;
	backendExperimentRef: React.RefObject<BackendExperiment>;
	clearReferrer: () => void | Promise<void>;
	cloudId: string | undefined;
	commonAttributes: CommonAttributes;
	edges: TEdge[];
	endCursor: string | null | undefined;
	experimentConfig: ExperimentConfig;
	getSelectedFilters: () => SelectedFiltersReturnType;
	intentDetectionTriggered?: boolean;
	isLoadingNext: boolean;
	isJiraIssueIndexed: boolean;
	isPaginatedQuery: boolean;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	productDataRegions: TProductDataRegions | undefined;
	productEdition: TProductEdition | undefined;
	productsWithTimeoutsFromErrors: ProductKeys[] | undefined;
	/** Identity of the loaded query whose results are currently rendered. */
	queryReference: object;
	referrer: string | undefined;
	resultsForAnalytics: IAnalyticsSearchPageResults;
	resultsShown: ResultShown[];
	searchQuery: string;
	/** Sites the search covered, keyed by cloudId. Undefined when not site-scoped. */
	selectedSites: ReadonlyMap<string, string> | undefined;
};

export const useSearchResultsAnalytics: <TEdge>({
	are3pResultsShown,
	backendExperimentRef,
	clearReferrer,
	cloudId,
	commonAttributes,
	edges,
	endCursor,
	experimentConfig,
	getSelectedFilters,
	intentDetectionTriggered,
	isLoadingNext,
	isJiraIssueIndexed,
	isPaginatedQuery,
	nonPrivacySafeAttributes,
	productDataRegions,
	productEdition,
	productsWithTimeoutsFromErrors,
	queryReference,
	referrer,
	resultsForAnalytics,
	resultsShown,
	searchQuery,
	selectedSites,
}: useSearchResultsAnalyticsArgs<TEdge>) => void = <TEdge>({
	are3pResultsShown,
	backendExperimentRef,
	clearReferrer,
	cloudId,
	commonAttributes,
	edges,
	endCursor,
	experimentConfig,
	getSelectedFilters,
	intentDetectionTriggered,
	isLoadingNext,
	isJiraIssueIndexed,
	isPaginatedQuery,
	nonPrivacySafeAttributes,
	productDataRegions,
	productEdition,
	productsWithTimeoutsFromErrors,
	queryReference,
	referrer,
	resultsForAnalytics,
	resultsShown,
	searchQuery,
	selectedSites,
}: useSearchResultsAnalyticsArgs<TEdge>) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const logException = useLogException();

	const intl = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const userId = useCurrentUserId();
	const intentDetectionCohort = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false, // Exposure is fired when a user calls the intent detection API
		locale: intl.locale,
	})?.cohort;

	// Properties related to social signals
	const socialSignalsAttributes = useSocialSignalsAnalyticsAttributes(resultsForAnalytics);

	// Properties related to the search results in the result set
	const { searchAndResult } = useSearchAndResultAnalyticsAttributes(resultsForAnalytics);

	// We store these values in a ref as the references to the properties are not
	// stable we do not want to trigger the analytics events unnecessarily. This
	// allows to narrow down the dependency array of the useEffect hook that fires
	// the analytics events.
	const analyticsProperties = {
		are3pResultsShown,
		clearReferrer,
		cloudId,
		commonAttributes,
		experimentConfig,
		fireAnalyticsEvent,
		getSelectedFilters,
		intentDetectionTriggered,
		intentDetectionCohort,
		isJiraIssueIndexed,
		isPaginatedQuery,
		logException,
		nonPrivacySafeAttributes,
		numResults: edges.length,
		productDataRegions,
		productEdition,
		productsWithTimeoutsFromErrors,
		referrer,
		resultsShown,
		searchAndResult,
		searchQuery,
		selectedSites,
		socialSignalsAttributes,
		userId,
	};
	const analyticsPropertiesRef = useRef(analyticsProperties);
	analyticsPropertiesRef.current = analyticsProperties;

	// Relay can replace edges when another query or hydration updates a result's fields.
	// Those updates render the same search again, but are not another search/page completion.
	const guardResultFieldChanges = fg('rovo_fps_results_analytics_field_guard');
	const resultsTrigger = guardResultFieldChanges ? queryReference : edges;
	const pageTrigger = guardResultFieldChanges ? endCursor : undefined;
	const previousAnalyticsTriggersRef = useRef<readonly unknown[]>();

	useEffect(() => {
		const analyticsTriggers = [resultsTrigger, pageTrigger, isLoadingNext, backendExperimentRef];
		const previousTriggers = previousAnalyticsTriggersRef.current;
		// Record committed transitions, including loading, so a completed page with the
		// same cursor still reports. Field-only updates continue through to bookkeeping.
		previousAnalyticsTriggersRef.current = analyticsTriggers;
		if (isLoadingNext) {
			return;
		}

		const shouldReportResults =
			!guardResultFieldChanges ||
			!previousTriggers ||
			previousTriggers.length !== analyticsTriggers.length ||
			analyticsTriggers.some((value, index) => !Object.is(value, previousTriggers[index]));

		const {
			are3pResultsShown,
			clearReferrer,
			commonAttributes: commonAttributesWithoutExtensionData,
			fireAnalyticsEvent,
			intentDetectionTriggered,
			intentDetectionCohort,
			isPaginatedQuery,
			logException,
			nonPrivacySafeAttributes,
			numResults,
			productsWithTimeoutsFromErrors,
			referrer: referrerFromStore,
			resultsShown,
			searchAndResult,
			searchQuery,
			selectedSites,
			socialSignalsAttributes,
			userId,
		} = analyticsPropertiesRef.current;

		if (shouldReportResults) {
			// Prefer the store value (kept in sync by the host app via onUpdate) but fall back to
			// reading directly from the URL at event-fire time. The fallback handles the case where
			// the host app does not update queryParams after a SPA navigation that adds ?referrer=
			// to the URL (e.g. zero-query suggestions navigating to the search page) and therefore
			// the store value is undefined even though the URL has a referrer.
			const referrerFromUrl = getReferrerFromUrl();
			const referrer = referrerFromStore ?? referrerFromUrl;

			const isBulkConnectBannerShown = getIsBulkConnectBannerShown();

			const commonAttributes = {
				...commonAttributesWithoutExtensionData,
				...getExtensionData(commonAttributesWithoutExtensionData.isCQL),
			};

			const requestedBackendExperiment: BackendExperiment =
				backendExperimentRef.current ?? undefined;

			fireAnalyticsEvent(
				onAdvancedSearchResultsShown({
					queryState: searchQuery ? QueryState.POSTQUERY : QueryState.PREQUERY,
					numResults,
					shownCount: numResults,
					are3pResultsShown,
					commonAttributes,
					nonPrivacySafeAttributes,
					socialSignalsAttributes,
					resultsShown,
					// Omitted rather than zero when the search was not site-scoped.
					selectedSitesCount: selectedSites?.size || undefined,
					availableSitesCount: getAvailableSitesCount(selectedSites),
					searchAndResult,
					requestedBackendExperiments: {
						backendExperiments: requestedBackendExperiment,
					},
					referrer: referrer && searchQuery ? referrer : undefined,
					isPaginatedQuery,
					isBulkConnectBannerShown,
					productsWithTimeout: productsWithTimeoutsFromErrors || [],
					...(intentDetectionTriggered !== undefined ? { intentDetectionTriggered } : {}),
					queryClassification: INTENT_DETECTION.value()?.mode,
					modelId: INTENT_DETECTION.value()?.modelId,
					cohort: intentDetectionCohort,
					patternType: INTENT_DETECTION.value()?.patternType,
					wordCount: commonAttributes.wordCount,
				}),
			);

			fireAnalyticsEvent(
				onFullPageSearchOperational({
					success: true,
					commonAttributes,
					userId,
				}),
			);
		}

		// Preserve referrer cleanup and load bookkeeping after analytics.
		searchResultsLoadUFOExperienceSuccess({
			queryLength: commonAttributesWithoutExtensionData.queryLength,
			wordCount: commonAttributesWithoutExtensionData.wordCount,
		});

		// Ensure that the referrer attribute is only used once
		clearReferrer();
		removeReferrerFromUrl(logException);
	}, [
		analyticsPropertiesRef,
		backendExperimentRef,
		guardResultFieldChanges,
		edges,
		resultsTrigger,
		pageTrigger,
		isLoadingNext,
	]);
};
